import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'urql';
import ButtonToast, { useButtonToast } from '@/components/Custom/ButtonToast';
import FormSaveButton from '@/components/Custom/FormSaveButton';
import ModalKeyboardAwareScrollView from '@/components/Custom/ModalKeyboardAwareScrollView';
import ModalKeyboardStickyView from '@/components/Custom/ModalKeyboardStickyView';
import EventEditInputTitle from '@/components/Event/EventEditInputTitle';
import EventEditTagGroup from '@/components/Event/EventEditTagGroup';
import OrganizationUpdateDangerZone from '@/components/Organization/OrganizationUpdateDangerZone';
import ContentBoundary from '@/components/UI/ContentBoundary';
import EmptyPlaceholder from '@/components/UI/EmptyPlaceholder';
import Box from '@/components/Unistyles/Box';
import { graphql, type ResultOf } from '@/gql/graphql';
import eventTypeMap from '@/utils/eventTypeMap';

const GetOrganizationEdit = graphql(`
  query GetOrganizationEdit($organizationId: uuid!) {
    event_by_pk(id: $organizationId) {
      id
      title
      tagGroupId: tag_group_id
      event_type
      permissions
    }
  }
`);

const UpdateOrganization = graphql(`
  mutation UpdateOrganization(
    $organizationId: uuid!
    $title: String!
    $tagGroupId: uuid
  ) {
    update_event_by_pk(
      pk_columns: { id: $organizationId }
      _set: { title: $title, tag_group_id: $tagGroupId }
    ) {
      id
      title
      tag_group_id
      root_owner {
        id
        tag_group {
          id
        }
      }
    }
  }
`);

type OrganizationEditFormValues = {
  title: string;
  tagGroupId: string | null;
};

function OrganizationEditForm({
  organization,
}: {
  organization: NonNullable<
    ResultOf<typeof GetOrganizationEdit>['event_by_pk']
  >;
}) {
  const form = useForm<OrganizationEditFormValues>({
    defaultValues: {
      title: organization.title ?? '',
      tagGroupId: organization.tagGroupId,
    },
    resolver: yupResolver(eventTypeMap.organization.validator),
  });
  const { back } = useRouter();
  const [, update] = useMutation(UpdateOrganization);
  const [toast, setToast] = useButtonToast();
  const canEdit = !!organization.permissions?.edit;
  const save = form.handleSubmit(async ({ title, tagGroupId }) => {
    const result = await update({
      organizationId: organization.id,
      title,
      tagGroupId: tagGroupId || null,
    });
    if (result.error || !result.data?.update_event_by_pk) {
      setToast({
        text: 'Could not save organization. Please try again.',
        variant: 'error',
        icon: 'close',
      });
      return;
    }
    back();
  });
  return (
    <FormProvider {...form}>
      <Stack.Screen
        options={{ title: organization.title || 'Edit organization' }}
      />
      <ModalKeyboardAwareScrollView>
        <Box gap="sm" p="md">
          {canEdit ? (
            <>
              <EventEditInputTitle />
              <EventEditTagGroup />
            </>
          ) : null}
          <OrganizationUpdateDangerZone organizationId={organization.id} />
        </Box>
      </ModalKeyboardAwareScrollView>
      {canEdit ? (
        <ModalKeyboardStickyView>
          <ButtonToast {...toast} />
          <FormSaveButton info={{ text: 'Save' }} onPress={save} />
        </ModalKeyboardStickyView>
      ) : null}
    </FormProvider>
  );
}

export default function OrganizationEdit() {
  const { organizationId } = useLocalSearchParams<{ organizationId: string }>();
  const [result, refresh] = useQuery({
    query: GetOrganizationEdit,
    variables: { organizationId },
  });
  const organization = result.data?.event_by_pk;
  return (
    <ContentBoundary
      data={organization?.event_type === 'organization' ? organization : null}
      loading={result.fetching}
      error={result.error}
      onRetry={() => refresh({ requestPolicy: 'network-only' })}
      empty={
        <EmptyPlaceholder message="Organization editing is unavailable." />
      }
    >
      {(organization) => (
        <OrganizationEditForm
          key={organization.id}
          organization={organization}
        />
      )}
    </ContentBoundary>
  );
}
